import React, { useEffect } from 'react';
import { Card, Col, Divider, Row, Typography } from 'antd';
import { HomeOutlined, RightOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { HOME_PATH, MENU_CATEGORIES } from '../../utils/constant';
import { useMediaQuery } from '../../hooks/MediaQuery.hook';
import './index.css';

const { Title, Paragraph, Text } = Typography;

const PAGE_TITLE =
  'Free Live Sports Streams – Soccer, NFL, NBA, UFC, F1 & More |Sportsfeed24';

const PAGE_DESCRIPTION =
  'Browse every sports category onSportsfeed24. Watch free HD live streams for soccer, football, NFL, NBA, UFC, boxing, F1, NHL, MLB, MLS, MotoGP and rugby — no subscription required.';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  Soccer: 'Premier League, Champions League, La Liga and more',
  NFL: 'Regular season, playoffs and Super Bowl',
  F1: 'Grand Prix races, qualifying and sprint sessions',
  NBA: 'Regular season and playoff basketball',
  Boxing: 'World-title fights and major events',
  'MMA/UFC': 'Fight Night cards and pay-per-view events',
  NHL: 'Ice hockey games and playoff series',
  MLS: 'Major League Soccer matches and fixtures',
  MLB: 'Baseball season games and playoffs',
  Motogp: 'Race weekends and championship rounds',
  Rugby: 'International and club rugby matches',
};

const CATEGORY_DETAILS: Record<string, { heading: string; paragraphs: string[] }> = {
  Soccer: {
    heading: 'Free Soccer & Football Live Streams',
    paragraphs: [
      'Soccer is the heart of SportsFeed24. We aggregate live football streams from the Premier League, La Liga, Serie A, Bundesliga, Ligue 1, UEFA Champions League, Europa League, and international tournaments — all in one place onsportsfeed24.',
      'SportsFeed24 does not charge for access. We list multiple player options per match so fans can switch streams if one buffers, and our daily schedule is updated before every kick-off. Whether you follow Manchester United, Real Madrid, or your local club, free soccer streams are always a click away.',
    ],
  },
  NFL: {
    heading: 'Free NFL Live Streams',
    paragraphs: [
      'American football fans rely on SportsFeed24 for every NFL Sunday, Monday Night Football, Thursday Night Football, playoff weekend, and the Super Bowl. Our NFL section lists every scheduled game with HD stream links as soon as they go live.',
      'SportsFeed24 removes the need for expensive cable packages or regional blackout restrictions. Open the NFL category page, pick your matchup, and watch free NFL streams on any device — phone, tablet, laptop, or smart TV browser.',
    ],
  },
  F1: {
    heading: 'Free Formula 1 Live Streams',
    paragraphs: [
      'Formula 1 race weekends move fast and SportsFeed24 keeps pace. We cover every Grand Prix, qualifying session, sprint race, and practice across the full F1 calendar — from Monaco to Abu Dhabi.',
      'Our platform curates reliable F1 stream links so you never miss a lap, pit stop, or championship-deciding moment. SportsFeed24 lists F1 events on the homepage and the dedicated F1 category page, updated throughout each race weekend at no cost.',
    ],
  },
  NBA: {
    heading: 'Free NBA Live Streams',
    paragraphs: [
      'Basketball runs year-round on SportsFeed24. From opening night through the playoffs and NBA Finals, we provide free live streams for regular season games, Christmas Day specials, All-Star Weekend, and every postseason series.',
      'SportsFeed24 makes NBA streaming simple: no account, no paywall. Select the NBA category, choose tonight\'s game, and enjoy HD basketball streams from tip-off to the final buzzer.',
    ],
  },
  Boxing: {
    heading: 'Free Boxing Live Streams',
    paragraphs: [
      'Major boxing events from WBA, WBC, IBF, and WBO promotions are listed on SportsFeed24 as they happen. World-title fights, championship unifications, and high-profile undercards all appear on our boxing streams page.',
      'SportsFeed24 connects fans to free boxing streams so you can watch every round live. We update links before the opening bell and offer multiple players when available, ensuring you stay with the action from walkouts to the final decision.',
    ],
  },
  'MMA/UFC': {
    heading: 'Free UFC & MMA Live Streams',
    paragraphs: [
      'Mixed martial arts fans turn to SportsFeed24 for UFC Fight Night cards, numbered events, and major pay-per-view bouts — all streamed free in HD. Prelims and main cards are covered so you catch every fight on the card.',
      'SportsFeed24 aggregates MMA stream links from across the web and presents them in a clean, easy-to-navigate layout. No PPV purchase required — open the MMA/UFC category onsportsfeed24 and watch live.',
    ],
  },
  NHL: {
    heading: 'Free NHL Live Streams',
    paragraphs: [
      'Ice hockey season means nightly NHL action, and SportsFeed24 delivers. Regular season games, Stanley Cup playoff rounds, and outdoor classics are all listed with live stream links on our NHL page.',
      'SportsFeed24 helps hockey fans skip expensive streaming subscriptions. Pick your team\'s game from the schedule, select a player, and watch free NHL streams in real time throughout the season.',
    ],
  },
  MLS: {
    heading: 'Free MLS Live Streams',
    paragraphs: [
      'Major League Soccer continues to grow, and SportsFeed24 covers the full MLS calendar — regular season, playoffs, and MLS Cup. Follow Inter Miami, LAFC, Atlanta United, and every club with free live streams.',
      'Our MLS category page is updated matchday by matchday. SportsFeed24 lists fixtures and stream links together so football fans in the US and worldwide can watch MLS free without a dedicated sports package.',
    ],
  },
  MLB: {
    heading: 'Free MLB Live Streams',
    paragraphs: [
      'Baseball fans get the full MLB season on SportsFeed24 — opening day through the World Series. Regular season matchups, rivalry games, and playoff series are all available with free HD stream links.',
      'SportsFeed24 simplifies MLB streaming by grouping today\'s games on one page. No blackout fees, no login — just pick a game and watch free baseball streams onsportsfeed24.',
    ],
  },
  Motogp: {
    heading: 'Free MotoGP Live Streams',
    paragraphs: [
      'MotoGP brings high-speed drama every race weekend, and SportsFeed24 covers qualifying, sprint races, and full Grand Prix events across the championship season.',
      'We list MotoGP stream links as sessions go live so fans never miss a start. SportsFeed24 provides free access to two-wheel racing alongside our F1 and broader motorsport coverage — all from one platform.',
    ],
  },
  Rugby: {
    heading: 'Free Rugby Live Streams',
    paragraphs: [
      'Rugby union and international fixtures — including Six Nations, Rugby Championship, club competitions, and World Cup matches — are streamed free on SportsFeed24.',
      'SportsFeed24 gathers rugby stream links for fans who want reliable, no-cost coverage. Visit the Rugby category page on match day, select your fixture, and watch live rugby streams in HD on any device.',
    ],
  },
};

const FAQ_ITEMS = [
  {
    question: 'Is SportsFeed24 really free?',
    answer:
      'Yes.Sportsfeed24 provides free live sports streams with no subscription, registration, or hidden fees. Open the site, pick a match, and start watching.',
  },
  {
    question: 'How does SportsFeed24 provide streams?',
    answer:
      'SportsFeed24 aggregates publicly available stream links and organises them by sport and fixture. We update schedules daily and offer multiple player options per event for the best viewing experience.',
  },
  {
    question: 'Which sports can I watch on SportsFeed24?',
    answer:
      'Soccer, NFL, NBA, UFC/MMA, boxing, F1, MotoGP, NHL, MLB, MLS, and rugby — all available from the category links on this page.',
  },
  {
    question: 'What devices work with SportsFeed24?',
    answer:
      'SportsFeed24 works on iOS, Android, Windows, Mac, and smart TV browsers. No app download is required.',
  },
];

const formatLabel = (label: string) =>
  label.length <= 3 ? label.toUpperCase() : label;

const setMetaTag = (name: string, content: string, attribute = 'name') => {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const setCanonical = (href: string) => {
  let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
};

export default function HomeLink() {
  const matches = useMediaQuery('(min-width: 768px)');

  useEffect(() => {
    document.title = PAGE_TITLE;
    setMetaTag('description', PAGE_DESCRIPTION);
    setMetaTag(
      'keywords',
      'sportsfeed24, free sports streams, soccer streams, NFL streams, NBA streams, UFC streams, F1 streams, live sports,sportsfeed24'
    );
    setMetaTag('og:title', PAGE_TITLE, 'property');
    setMetaTag('og:description', PAGE_DESCRIPTION, 'property');
    setMetaTag('og:url', 'https://sportsfeed24.st/home-link', 'property');
    setCanonical('https://sportsfeed24.st/home-link');

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'home-link-schema';
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'SportsFeed24 – Free Live Sports Streams by Category',
      description: PAGE_DESCRIPTION,
      url: 'https://sportsfeed24.st/home-link',
      isPartOf: {
        '@type': 'WebSite',
        name: 'SportsFeed24',
        url: 'https://sportsfeed24.st',
      },
      mainEntity: {
        '@type': 'ItemList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Homepage – All Live Sports',
            url: 'https://sportsfeed24.st/',
          },
          ...MENU_CATEGORIES.map((category, index) => ({
            '@type': 'ListItem',
            position: index + 2,
            name: `${category.label} Live Streams`,
            url: `https://sportsfeed24.st/streams/${category.value}`,
          })),
        ],
      },
    });
    document.head.appendChild(schemaScript);

    return () => {
      document.getElementById('home-link-schema')?.remove();
    };
  }, []);

  return (
    <article className='home-link-page' style={{ padding: matches ? '32px 24px' : '16px 12px' }}>
      <header className='home-link-hero'>
        <Title level={1} className='home-link-title'>
          Sports<span className='home-link-accent'>Feed</span>24 – Free Live Sports Streams by Category
        </Title>
        <Paragraph className='home-link-subtitle'>
         Sportsfeed24 is your free gateway to live sports streaming. Browse every category below —
          soccer, NFL, NBA, UFC, boxing, F1, and more — and watch HD matches with no subscription or sign-up.
        </Paragraph>
      </header>

      <section aria-label='Sports category links'>
        <Title level={2} className='home-link-section-title'>Browse Live Streams</Title>
        <Row gutter={[16, 16]} justify='center'>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Link to={HOME_PATH} className='home-link-card-link'>
              <Card hoverable className='home-link-card home-link-card--featured'>
                <div className='home-link-card-icon'>
                  <HomeOutlined />
                </div>
                <Title level={3} className='home-link-card-title'>Homepage</Title>
                <Text className='home-link-card-desc'>
                  Today&apos;s live matches across all sports
                </Text>
                <RightOutlined className='home-link-card-arrow' />
              </Card>
            </Link>
          </Col>

          {MENU_CATEGORIES.map((category) => {
            const path = `/streams/${category.value}`;
            const description =
              CATEGORY_DESCRIPTIONS[category.label] ?? `Watch live ${category.label} streams`;

            return (
              <Col key={`${category.label}-${category.value}`} xs={24} sm={12} md={8} lg={6}>
                <Link to={path} className='home-link-card-link'>
                  <Card hoverable className='home-link-card'>
                    <Title level={3} className='home-link-card-title'>
                      {formatLabel(category.label)}
                    </Title>
                    <Text className='home-link-card-desc'>{description}</Text>
                    <Text className='home-link-card-path'>{category.label} Streams</Text>
                    <RightOutlined className='home-link-card-arrow' />
                  </Card>
                </Link>
              </Col>
            );
          })}
        </Row>
      </section>

      <Divider className='home-link-divider' />

      <section className='home-link-about' aria-label='About SportsFeed24 free streaming'>
        <Title level={2} className='home-link-section-title'>
          How SportsFeed24 Delivers Free Live Sports Streams
        </Title>
        <Paragraph className='home-link-body-text'>
          SportsFeed24 was built for fans who want one reliable place to watch live sports without paying
          for cable, PPV, or multiple streaming subscriptions. Every day we update our fixture list with
          today&apos;s matches across every major sport, then organise free HD stream links by category so
          you can find what you need in seconds.
        </Paragraph>
        <Paragraph className='home-link-body-text'>
          Our role is simple: we connect you to live sports streams quickly and safely. SportsFeed24 does
          not require an account, does not store payment details, and works on any modern browser. Pick a
          category, choose your match, select a player, and enjoy uninterrupted free sports streaming on
         sportsfeed24.
        </Paragraph>
      </section>

      <section className='home-link-categories' aria-label='Sports category guides'>
        <Title level={2} className='home-link-section-title'>
          Every Sport Category on SportsFeed24 Explained
        </Title>

        {MENU_CATEGORIES.map((category) => {
          const path = `/streams/${category.value}`;
          const details = CATEGORY_DETAILS[category.label];

          return (
            <div key={`detail-${category.label}`} className='home-link-category-block'>
              <Title level={3} className='home-link-category-heading'>
                <Link to={path}>{details?.heading ?? `${category.label} Live Streams`}</Link>
              </Title>
              {(details?.paragraphs ?? [
                `SportsFeed24 provides free live ${category.label} streams updated daily onsportsfeed24.`,
              ]).map((paragraph, index) => (
                <Paragraph key={index} className='home-link-body-text'>
                  {paragraph}
                </Paragraph>
              ))}
              <Link to={path} className='home-link-category-cta'>
                Watch free {formatLabel(category.label)} streams <RightOutlined />
              </Link>
            </div>
          );
        })}
      </section>

      <section className='home-link-faq' aria-label='Frequently asked questions'>
        <Title level={2} className='home-link-section-title'>
          Sports Streaming FAQ
        </Title>
        {FAQ_ITEMS.map((item) => (
          <div key={item.question} className='home-link-faq-item'>
            <Title level={4} className='home-link-faq-question'>{item.question}</Title>
            <Paragraph className='home-link-body-text'>{item.answer}</Paragraph>
          </div>
        ))}
      </section>
    </article>
  );
};